import {
  Button as BaseButton,
  Loader,
  Modal
} from '@120wateraudit/envirio-components'
import { useLazyQuery } from '@apollo/client'
import FileSaver from 'file-saver'
import React, { useEffect, useState } from 'react'
import { PDFReader } from 'reactjs-pdf-reader'
import { Container } from 'semantic-ui-react'
import { contactsClient } from 'src/apollo-clients'
import { ReactComponent as Print } from 'src/assets/images/icon-print.svg'
import { ReactComponent as Save } from 'src/assets/images/icon-save.svg'
import { CommunicationInput } from 'src/containers/Communications/EditorSuite/data-access'
import {
  DispatchType,
  LayoutContainerActions
} from 'src/containers/Communications/EditorSuite/layoutContainerReducer'
import { useCreatePDFResources } from 'src/hooks'
import styled from 'styled-components'
import {
  COMMUNICATION_PREVIEW,
  CommunicationPreviewData,
  CommunicationPreviewVariables
} from './dataAccess'

export const PDFArchiveViewer: React.FC<{
  isOpen: boolean
  title: string
  url: string
  toggle: () => void
}> = ({ isOpen, title, toggle, url }) => {
  return <PDFModal isOpen={isOpen} title={title} toggle={toggle} url={url} />
}

export const PDFPreview = ({
  accountId,
  createCommunicationPreview,
  dispatch,
  isOpen,
  toggle
}: Props) => {
  const [state, setState] = useState<undefined | State>(undefined)

  const [communicationPreviewQuery, { data, error, loading }] = useLazyQuery<
    CommunicationPreviewData,
    CommunicationPreviewVariables
  >(COMMUNICATION_PREVIEW, {
    client: contactsClient,
    fetchPolicy: 'network-only',
    variables: {
      accountId,
      ...(state as State)
    }
  })

  useEffect(() => {
    const runQuery = async () => {
      const params = await createCommunicationPreview()
      setState(params)

      communicationPreviewQuery()
    }
    runQuery()
  }, [createCommunicationPreview, communicationPreviewQuery])

  useEffect(() => {
    if (error && dispatch) {
      dispatch({ payload: error, type: DispatchType.Error })
      toggle()
    }
  }, [error, dispatch, toggle])

  const name =
    state?.communication?.name || state?.communication?.communicationType
  const title = `${name}-${new Date().toISOString()}`

  return (
    <PDFModal
      isOpen={isOpen}
      loading={loading || !data}
      template={data && data.communicationPreview}
      title={title}
      toggle={toggle}
    />
  )
}

export const PDFModal: React.FC<{
  isOpen: boolean
  loading?: boolean
  template?: string
  title: string
  url?: string
  toggle: () => void
}> = ({ isOpen, loading = false, template, title, toggle, url }) => (
  <Modal
    content={
      <Container>
        {loading ? (
          <Loader />
        ) : (
          <PDFViewer
            archiveUrl={url}
            template={template}
            toggle={toggle}
            title={title}
          />
        )}
      </Container>
    }
    isOpen={isOpen}
    overflowStyle="hidden"
    style={{
      content: {
        maxWidth: 'fit-content'
      }
    }}
    toggle={toggle}
  />
)

export default PDFModal

const PDFViewer = ({
  archiveUrl,
  template,
  title,
  toggle
}: {
  archiveUrl?: string
  template?: string
  title: string
  toggle: () => void
}) => {
  const { blob, loading, url } = useCreatePDFResources({
    archiveUrl,
    htmlString: template
  })

  const download = () => {
    FileSaver.saveAs(blob, `${title}.pdf`)
  }

  if (loading) {
    return <Loader />
  }

  return (
    <>
      <ButtonsWrapper>
        <Button onClick={download} size="small">
          <Save />
        </Button>
        <Button onClick={() => window.open(url)} size="small">
          <Print style={{ height: '16px', width: 'auto' }} />
        </Button>
        <Button onClick={toggle} size="small" style={{ marginRight: 0 }}>
          Close Preview
        </Button>
      </ButtonsWrapper>
      <TestViewer>
        <PDFReader scale={1.25} showAllPage url={url} />
      </TestViewer>
    </>
  )
}

interface Props {
  accountId: number
  communicationId?: number
  dispatch?: React.Dispatch<LayoutContainerActions>
  isOpen: boolean
  createCommunicationPreview: () => Promise<{
    communication: CommunicationInput
    content: string
  }>
  toggle: () => void
}

interface State {
  communication: CommunicationInput
  content: string
}

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: sticky;
  margin-bottom: 1em;
`

const Button = styled(BaseButton)`
  border-radius: 5px;
  min-width: auto;
  height: 3rem;

  & img {
    width: 14px;
    height: 14px;
  }
`

const TestViewer = styled.div`
  overflow-y: scroll;
  overflow-x: hidden;
  height: calc(84vh - 40px);
  padding: 20px;
  background: lightgrey;
  border-top: 1px solid lightgrey;
`
